import React from 'react';

import { Footer } from '../components/footer';
import { Camp67Header } from '../components/camp67-header/camp67-header';

import { Link } from '@remix-run/react';
import { Page } from '../pages/pages-types';
import clsx from 'clsx';
import { pageStore } from '../pages/page-store';
import { FormattedString, useMessages } from '../i18n/useMessages';

export const ContentPage: React.FC<Page> = ({
  heroImage,
  heroImageAlt,
  title: titleKey,
  readingTime,
  summary,
  content,
  linkedPages,
  maxWidth = false,
}) => {
  const getMessage = useMessages();

  const allLinkedPages = linkedPages?.(pageStore) ?? [];
  const title = getMessage(titleKey);

  return (
    <>
      {/* Hero Section */}
      <section className="w-full relative h-[450px] md:h-[550px]">
        <Camp67Header isLogoLight={true} isDarkGlowHidden={false} isSunHidden={true} />
        <img
          className="h-full w-full object-cover -z-10 absolute top-0 left-0 object-center"
          alt={heroImageAlt}
          src={heroImage}
        />
        {/* Dark overlay for text readability - stronger gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />

        {/* Hero Content - Left aligned with dark background band at bottom */}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent pt-20 pb-12 md:pb-16">
          <div className="w-full px-6 sm:px-12 md:px-24 lg:px-32 max-w-7xl mx-auto">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/20 backdrop-blur-sm text-white/90 text-xs font-medium uppercase tracking-wider rounded-full mb-4 border border-white/10">
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {readingTime}{' '}
              <FormattedString message="camp67-content-page.child.reading-time" />
            </span>
            <h1 className="text-white font-bold text-3xl md:text-4xl lg:text-5xl font-serif leading-tight max-w-3xl">
              {title}
            </h1>
            {summary && (
              <div className="text-white/85 text-base md:text-lg leading-relaxed mt-4 max-w-2xl">
                {summary as React.ReactNode}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Main Content - More spacing from hero */}
      <main className="bg-yellow-50 pt-16 md:pt-20 pb-12 md:pb-16">
        <div className="w-full px-6 sm:px-12 md:px-24 lg:px-32 max-w-7xl mx-auto">
          {/* Content Area */}
          <article
            className={clsx(
              'prose prose-lg',
              'prose-headings:font-serif prose-headings:text-gray-900 prose-headings:font-bold',
              'prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-5',
              'prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4',
              'prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-5',
              'prose-strong:text-gray-900 prose-strong:font-semibold',
              'prose-a:text-orange-600 prose-a:font-medium prose-a:underline prose-a:underline-offset-2',
              'prose-ul:my-5 prose-li:text-gray-700 prose-li:marker:text-orange-500',
              maxWidth ? 'max-w-none' : 'max-w-3xl',
            )}
          >
            {content}
          </article>
        </div>

        {/* Linked Pages Section */}
        {allLinkedPages && allLinkedPages.length > 0 && (
          <section className="mt-16 md:mt-20 pt-12 border-t border-orange-200">
            <div className="w-full px-6 sm:px-12 md:px-24 lg:px-32 max-w-7xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-8 h-0.5 bg-orange-400" />
                <h2 className="text-sm font-semibold uppercase tracking-wider text-orange-700">
                  <FormattedString message="camp67-content-page.read-more" />
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl">
                {allLinkedPages.map(
                  (
                    {
                      path,
                      readingTime: linkedReadingTime,
                      subtitle,
                      title: linkedTitle,
                      heroImage: linkedHeroImage,
                    },
                    index,
                  ) => (
                    <Link key={index} prefetch="intent" to={path} className="group">
                      <article className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 h-full flex flex-col border border-gray-100 hover:border-orange-200">
                        {linkedHeroImage && (
                          <div className="h-36 overflow-hidden">
                            <img
                              src={linkedHeroImage}
                              alt=""
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                        )}
                        <div className="p-5 flex flex-col flex-1">
                          <h3 className="font-serif text-gray-900 group-hover:text-orange-600 text-lg font-bold transition-colors">
                            <FormattedString message={linkedTitle} />
                          </h3>
                          {subtitle && (
                            <p className="text-gray-600 text-sm mt-2 line-clamp-2 flex-1">
                              <FormattedString message={subtitle} />
                            </p>
                          )}
                          <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                            <span className="text-xs text-gray-500 uppercase tracking-wider">
                              {linkedReadingTime}{' '}
                              <FormattedString message="camp67-content-page.child.reading-time" />
                            </span>
                            <span className="text-orange-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                              Read &rarr;
                            </span>
                          </div>
                        </div>
                      </article>
                    </Link>
                  ),
                )}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer isHome={false} />
    </>
  );
};
